import { authenticationService } from "@authentication/services/AuthenticationService";
import { parseParams } from "@authentication/utils";
import { BaseForm } from "@forms/core/BaseForm";
import { StringField } from "@forms/core/StringField";
import { goTo } from "@history";
import { FrontSiteRoutes } from "@routes/urls";
import { getIn } from "formik";
import _isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import React, { Component } from "react";
import Overridable from "react-overridable";
import { Container, Message, Checkbox } from "semantic-ui-react";
import { WelcomeManager } from "@utils/WelcomeGuideUtils";
import { AuditService } from "@api/audit/audit";

const LoginWithLocalAccountLayout = ({
  errorHeader,
  errorMessage,
  hasError,
  data,
  buttons,
  onSubmit,
  isSubmitting,
  showPassword,
  onTogglePassword,
}) => {
  return (
    <Overridable
      id="LoginWithLocalAccount.layout"
      data={data}
      buttons={buttons}
      onSubmit={onSubmit}
    >
      <Container
        fluid
        id="login-with-local-account-form"
        className="bottom-spaced"
      >
        <BaseForm initialValues={data} buttons={buttons} onSubmit={onSubmit}>
          <StringField
            fieldPath="email"
            placeholder="Email Address"
            type="email"
            icon="user"
            iconPosition="left"
            autoFocus
          />
          <StringField
            key={showPassword ? "password-text" : "password-hidden"}
            fieldPath="password"
            placeholder="Password"
            type={showPassword ? "text" : "password"}
            icon="lock"
            iconPosition="left"
          />

          <div style={{ margin: "0 0 0.5rem ", textAlign: "left" }}>
            <Checkbox
              label="Show Password"
              checked={showPassword}
              onChange={onTogglePassword}
            />
          </div>

          {hasError && (
            <Message negative header={errorHeader} content={errorMessage} />
          )}
          {isSubmitting && (
            <Message
              info
              header="Signing In..."
              content="Please wait while we sign you in."
            />
          )}
        </BaseForm>
      </Container>
    </Overridable>
  );
};

class LoginWithLocalAccount extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: {
        email: "",
        password: "",
      },
      isSubmitting: false,
      hasError: false,
      errorMessage: "",
      errorHeader: "Login Error",
      showPassword: false,
    };
  }

  get buttons() {
    const { isSubmitting } = this.state;
    return [
      {
        name: "login",
        content: isSubmitting ? "Signing In..." : "Sign in",
        primary: true,
        type: "submit",
        fluid: true,
        loading: isSubmitting,
        disabled: isSubmitting,
      },
    ];
  }

  validateForm = (values) => {
    const errors = {};

    if (!values.email) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(values.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!values.password) {
      errors.password = "Password is required";
    }

    return errors;
  };

  onTogglePassword = () => {
    this.setState((prev) => ({ showPassword: !prev.showPassword }));
  };

  onSubmit = async (values, actions) => {
    try {
      this.setState({
        isSubmitting: true,
        hasError: false,
        errorMessage: "",
        errorHeader: "Login Error",
      });
      actions.setSubmitting(true);

      const validationErrors = this.validateForm(values);
      if (Object.keys(validationErrors).length > 0) {
        actions.setErrors(validationErrors);
        this.setState({ isSubmitting: false });
        actions.setSubmitting(false);
        return;
      }

      const response = await authenticationService.loginWithLocalAccount(
        values
      );

      const { user } = this.props;
      const userEmail =
        user?.fullName || user?.username || values.email || "Unknown User";
      await AuditService.api.createLog({
        action: "Login",
        details: `User ${userEmail} logged in successfully via local account.`,
        created_by: `${userEmail}`,
      });

      this.handleWelcomeCheck(response);
      this.onSuccess(response);

      actions.setSubmitting(false);
      this.setState({ isSubmitting: false });
    } catch (error) {
      const errors = getIn(error, "response.data.errors", []);
      let errorMessage = "Login failed. Please try again.";
      let errorHeader = "Login Error";

      if (!_isEmpty(errors)) {
        const errorData = error.response.data;
        const payload = {};
        for (const fieldError of errorData.errors) {
          payload[fieldError.field] = fieldError.message;
        }
        actions.setErrors(payload);
      } else {
        const message = getIn(
          error,
          "response.data.message",
          getIn(error, "message", "Unknown error")
        );

        if (error.response?.status === 401) {
          errorHeader = "Invalid Credentials";
          errorMessage = "Email or password is incorrect. Please try again.";
        } else if (error.response?.status === 400) {
          errorHeader = "Account Disabled";
          errorMessage =
            "Your account is deactivated. A confirmation wil be sent to your e-mail.";
        } else if (error.response?.status === 403) {
          errorHeader = "Invalid Request";
          errorMessage = "Please check your email and password.";
        } else if (error.response?.status >= 500) {
          errorHeader = "Server Error";
          errorMessage = "Server error. Please try again later.";
        } else if (
          error.message?.includes("Network Error") ||
          error.message?.includes("CORS")
        ) {
          errorHeader = "Connection Error";
          errorMessage =
            "Unable to connect to login service. Please check your internet connection.";
        } else {
          errorMessage = message;
        }

        setTimeout(() => {
          window.location.reload();
        }, 5000);

        actions.setErrors({ message: errorMessage });
        this.setState({
          hasError: true,
          errorMessage,
          errorHeader,
        });
      }

      actions.setSubmitting(false);
      this.setState({ isSubmitting: false });
    }
  };

  handleWelcomeCheck = (response) => {
    const user = response.data?.user;
    if (user) {
      const shouldShowWelcome =
        WelcomeManager.shouldShowWelcomeAfterLogin(user);

      if (shouldShowWelcome) {
        WelcomeManager.setShowWelcomeFlag(user);
      }
    }
  };

  onSuccess = (response) => {
    const { fetchUserProfile } = this.props;
    const params = parseParams(window.location.search);

    fetchUserProfile();

    const nextUrl =
      params.next && params.next !== FrontSiteRoutes.home
        ? params.next
        : FrontSiteRoutes.dashboard;

    setTimeout(() => {
      goTo(nextUrl);
    }, 100);
  };

  render() {
    const {
      hasError: propsHasError,
      errorHeader: propsErrorHeader,
      errorMessage: propsErrorMessage,
    } = this.props;

    const {
      data,
      isSubmitting,
      showPassword,
      hasError,
      errorHeader,
      errorMessage,
    } = this.state;

    const displayError = hasError || propsHasError;
    const displayErrorHeader = errorHeader || propsErrorHeader;
    const displayErrorMessage = errorMessage || propsErrorMessage;

    return (
      <LoginWithLocalAccountLayout
        {...this.props}
        data={data}
        buttons={this.buttons}
        onSubmit={this.onSubmit}
        errorHeader={displayErrorHeader}
        errorMessage={displayErrorMessage}
        hasError={displayError}
        isSubmitting={isSubmitting}
        showPassword={showPassword}
        onTogglePassword={this.onTogglePassword}
      />
    );
  }
}

LoginWithLocalAccount.propTypes = {
  fetchUserProfile: PropTypes.func.isRequired,
  user: PropTypes.object,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  errorHeader: PropTypes.string,
  onTogglePassword: PropTypes.func, // Add this line
};

LoginWithLocalAccount.defaultProps = {
  user: null,
  onTogglePassword: () => {},
  hasError: false,
  errorHeader: "",
  errorMessage: "",
};

LoginWithLocalAccountLayout.propTypes = {
  data: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
  buttons: PropTypes.array.isRequired,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  errorHeader: PropTypes.string,
  isSubmitting: PropTypes.bool,
  showPassword: PropTypes.bool,
  onTogglePassword: PropTypes.func,
};

LoginWithLocalAccountLayout.defaultProps = {
  hasError: false,
  errorHeader: "",
  errorMessage: "",
  isSubmitting: false,
  showPassword: false,
};

export default Overridable.component(
  "LoginWithLocalAccount",
  LoginWithLocalAccount
);
